import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getUsers, getSites, getAssets, getWorkOrders, getInvoices, getStores, createSite, updateUser, createUser, updateSite, deleteSite, deleteUser, getXeroStatus, getAllXeroContacts, chaseCustomerOverdue, mergeCustomers, getCustomerRateCardSummary, resendUserInvite, getInviteLink, sendUserPasswordReset, inviteCustomerContact, getContactPortalStatus } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { formatDate } from '../../utils/date';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { ArrowLeft, MapPin, Boxes, ArrowRight, ClipboardList, Building2, Mail, Phone, Plus, Pencil, Trash2, Crosshair, AlertTriangle, Send, Wallet, AlertCircle, ShieldAlert, CheckCircle2, Warehouse, GitMerge, PoundSterling, Home, GitBranch, UserCog, KeyRound, Copy, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { SiteMapPicker } from '../../components/SiteMapPicker';
import { UkAddressFields, formatAddress, hasAddress } from '../../components/UkAddressFields';
import { XeroAddressSyncBanner } from '../../components/XeroAddressSyncBanner';
import { SiteSafetyDocDialog } from '../../components/SiteSafetyDocDialog';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { CustomerPaymentTermsCard } from '../../components/CustomerPaymentTermsCard';
import { SitePerformance } from '../../components/SitePerformance';
import { useAuth } from '../../context/AuthContext';
import RecipientStack from '../../components/RecipientStack';
import { ChaseEmailComposer } from '../../components/ChaseEmailComposer';

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [sites, setSites] = useState([]);
  const [stores, setStores] = useState([]);
  const [assets, setAssets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  // Full customer list — used by the merge dialog's picker.
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add-site dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [siteForm, setSiteForm] = useState({
    name: '', address: '', lat: null, lng: null, geofenceMeters: 250, isLutromHQ: false,
  });
  const [savingSite, setSavingSite] = useState(false);

  // Edit-customer dialog state
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [customerForm, setCustomerForm] = useState({ name: '', email: '', phone: '', company: '', title: '', role: 'customer', xeroContactId: '', customerType: 'business', notes: '', address: '', website: '' });
  const [savingCustomer, setSavingCustomer] = useState(false);

  // Xero contacts (loaded lazily for the Link-to-Xero picker inside the edit dialog)
  const [xeroConnected, setXeroConnected] = useState(false);
  const [xeroContacts, setXeroContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Edit-site dialog state (re-uses the same dialog for create + edit)
  const [editingSite, setEditingSite] = useState(null);
  const [editSiteOpen, setEditSiteOpen] = useState(false);
  const [editSiteForm, setEditSiteForm] = useState({
    name: '', address: '', lat: null, lng: null, geofenceMeters: 250, isLutromHQ: false,
  });
  const [savingEditSite, setSavingEditSite] = useState(false);

  // Site safety-doc editor (feeds the AI RAMS generator)
  const [safetyDocSite, setSafetyDocSite] = useState(null);
  const [safetyDocOpen, setSafetyDocOpen] = useState(false);

  // Chase-overdue dialog state
  const [chaseOpen, setChaseOpen] = useState(false);
  const [chasing, setChasing] = useState(false);
  // 72h throttle response from the server — when set, the dialog shows a
  // warning + "Send anyway" button instead of triggering window.confirm
  // (native confirm sits behind the Radix overlay on mobile Safari, which
  // is why admins reported the Chase button felt broken).
  const [chaseThrottle, setChaseThrottle] = useState(null);
  // Recipient picker state for the chase dialog. Mirrors the invoice
  // email dialog on AdminInvoiceDetail.jsx so admins can multi-select
  // from the customer's contact roster, free-text add, and CC.
  const [chaseTo, setChaseTo] = useState([]);           // string[]
  const [chaseCc, setChaseCc] = useState([]);           // string[]
  const [chaseManual, setChaseManual] = useState('');   // free-text input
  const [chaseSaveDefault, setChaseSaveDefault] = useState(false);
  const [chaseComposerOpen, setChaseComposerOpen] = useState(false);

  // Merge dialog state — lets the admin pick another customer and fold
  // this one into it (every site/asset/WO/invoice/team user is rewritten
  // to the winner, this row optionally deleted).
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [mergeDirection, setMergeDirection] = useState('into-target'); // 'into-target' | 'from-target'
  const [merging, setMerging] = useState(false);

  // Effective rate card for this customer (best-matching rule per band,
  // treating engineer as wildcard). Lazily loaded — lives on its own
  // endpoint so we don't block the page paint on it.
  const [rateCard, setRateCard] = useState(null);

  const refresh = () => Promise.all([getSites(), getAssets(), getWorkOrders(), getUsers()])
    .then(([s, a, w, users]) => {
      // Sub-customers whose parent is this customer — their sites/assets
      // roll up into this dashboard tagged with "via [Sub Name]".
      const subs = (users || []).filter(u => u.role === 'customer' && u.parentCustomerId === customerId);
      const subMap = new Map(subs.map(u => [u.id, u.company || u.name || '']));
      const inScope = new Set([customerId, ...subs.map(u => u.id)]);
      const tag = (row) => ({
        ...row,
        viaSubCustomerId: row.customerId !== customerId ? row.customerId : '',
        viaSubCustomerName: row.customerId !== customerId ? (subMap.get(row.customerId) || '') : '',
      });
      setSites(s.filter(x => inScope.has(x.customerId)).map(tag));
      setAssets(a.filter(x => inScope.has(x.customerId)).map(tag));
      setOrders(w.filter(x => x.customerId === customerId));
    });

  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    Promise.all([getUsers(), getSites(), getAssets(), getWorkOrders(), getInvoices(), getStores().catch(() => [])])
      .then(([users, s, a, w, inv, st]) => {
        const me = users.find(u => u.id === customerId) || null;
        setCustomer(me);
        // Keep the full customer list so `SubCustomersCard` can render
        // children whose `parentCustomerId` points at this record. The
        // merge-target picker filters this list itself when it renders.
        setAllCustomers(users.filter(u => u.role === 'customer' && u.id !== customerId));

        // Sub-customers whose parent is THIS record.
        const subs = users.filter(u => u.role === 'customer' && u.parentCustomerId === customerId);
        const subMap = new Map(subs.map(u => [u.id, u.company || u.name || '']));
        const inScope = new Set([customerId, ...subs.map(u => u.id)]);
        const tag = (row) => ({
          ...row,
          viaSubCustomerId: row.customerId !== customerId ? row.customerId : '',
          viaSubCustomerName: row.customerId !== customerId ? (subMap.get(row.customerId) || '') : '',
        });

        const scopedSites = s.filter(x => inScope.has(x.customerId)).map(tag);
        const scopedAssets = a.filter(x => inScope.has(x.customerId)).map(tag);
        setSites(scopedSites);
        setAssets(scopedAssets);

        // Work orders — two paths:
        // • Top-level customer: WOs are normalised to `customerId === parent.id`
        //   on the backend, so the direct filter already captures sub-customer work.
        // • Sub-customer view: `customerId` points at the parent, so we
        //   fall back to matching WOs via siteId belonging to this
        //   sub-customer's sites (billing still shows on the parent's
        //   dashboard, this view surfaces "what's happening here").
        const siteIds = new Set(scopedSites.map(x => x.id));
        if (me?.parentCustomerId) {
          setOrders(w.filter(x => x.subCustomerId === customerId || (x.siteId && siteIds.has(x.siteId))));
          setInvoices([]); // sub-customer directory has no invoices of its own
        } else {
          setOrders(w.filter(x => x.customerId === customerId));
          setInvoices((inv || []).filter(x => x.customerId === customerId));
        }
        setStores((st || []).filter(x => x.customerId === customerId));
      })
      .finally(() => setLoading(false));
    getXeroStatus().then(s => setXeroConnected(!!s?.connected)).catch(() => {});
    // Background-load the effective rate card. Never blocks the page.
    getCustomerRateCardSummary(customerId)
      .then(setRateCard)
      .catch(() => setRateCard(null));
  }, [customerId, reloadKey]);

  // Lazy-load Xero contacts once we know Xero is connected — used by the
  // "Link to Xero" picker inside the Edit Customer dialog.
  useEffect(() => {
    if (!xeroConnected || xeroContacts.length > 0 || loadingContacts) return;
    setLoadingContacts(true);
    getAllXeroContacts()
      .then((contacts) => setXeroContacts(contacts || []))
      .catch(() => {})
      .finally(() => setLoadingContacts(false));
  }, [xeroConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const sitesEnriched = useMemo(() => sites.map(s => ({
    ...s,
    assetCount: assets.filter(a => a.siteId === s.id).length,
    openWorkOrders: orders.filter(w => w.siteId === s.id && w.status !== 'completed').length,
  })), [sites, assets, orders]);

  // ------------- Chase-overdue recipient picker helpers -------------
  // Roster of possible chase recipients: the customer themselves + any
  // sub-customers underneath them + every embedded `contacts[]` row on
  // each of those users. Sub-customers get a "Sub" pill so the admin
  // doesn't accidentally email the sub-account when they meant the
  // parent payer.
  const chaseContacts = useMemo(() => {
    if (!customer) return [];
    const subs = (allCustomers || []).filter(u => u.parentCustomerId === customer.id);
    const roster = [customer, ...subs];
    const seen = new Set();
    const rows = [];
    const push = (row) => {
      const key = (row.email || '').trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      rows.push(row);
    };
    for (const u of roster) {
      push({
        id: u.id,
        email: (u.email || '').trim(),
        name: u.company || u.name || '',
        isPrimary: u.id === customer.id,
        source: 'customer',
      });
      for (const sub of (u.contacts || [])) {
        const label = [sub.name, sub.department, sub.role || sub.title].filter(Boolean).join(' · ');
        const suffix = (u.id === customer.id) ? '' : ` @ ${u.company || u.name}`;
        push({
          id: `${u.id}:${(sub.email || '').toLowerCase()}`,
          email: (sub.email || '').trim(),
          name: (label + suffix) || sub.email || '',
          isPrimary: false,
          source: 'contact',
        });
      }
    }
    // Primary first, then everything else in insertion order.
    rows.sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1));
    return rows;
  }, [customer, allCustomers]);

  const isValidChaseEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());

  const removeChaseEmail = (email) => {
    const lower = (email || '').toLowerCase();
    setChaseTo((prev) => prev.filter((e) => e.toLowerCase() !== lower));
    setChaseCc((prev) => prev.filter((e) => e.toLowerCase() !== lower));
  };

  // Move (or add) an email into the named bucket. Removes from the other
  // bucket so an address never lands in both.
  const assignChaseEmail = (email, bucket) => {
    const trimmed = (email || '').trim();
    if (!isValidChaseEmail(trimmed)) {
      toast.error('Enter a valid email');
      return;
    }
    const lower = trimmed.toLowerCase();
    setChaseTo((prev) => prev.filter((e) => e.toLowerCase() !== lower).concat(bucket === 'to' ? [trimmed] : []));
    setChaseCc((prev) => prev.filter((e) => e.toLowerCase() !== lower).concat(bucket === 'cc' ? [trimmed] : []));
  };

  const chaseBucket = (email) => {
    const lower = (email || '').toLowerCase();
    if (chaseTo.some((e) => e.toLowerCase() === lower)) return 'to';
    if (chaseCc.some((e) => e.toLowerCase() === lower)) return 'cc';
    return null;
  };

  const addChaseManual = (bucket) => {
    const v = chaseManual.trim();
    if (!v) return;
    assignChaseEmail(v, bucket);
    setChaseManual('');
  };

  // Open the chase dialog with sane defaults:
  //  - TO seeded with the customer's stored primary email (if any).
  //  - CC seeded with sibling primary emails (mirrors the server-side
  //    auto-cc default so the admin can see who *would* have been cc'd
  //    and remove them if unwanted).
  const openChaseDialog = () => {
    setChaseThrottle(null);
    // No company mailbox → default to an Accounts contact, else the first contact.
    const own = (customer?.contacts || []).filter(c => (c.email || '').trim());
    const accounts = own.find(c => /account/i.test(c.department || ''));
    const primary = (customer?.email || accounts?.email || own[0]?.email || '').trim();
    setChaseTo(primary ? [primary] : []);
    const siblingCcs = (allCustomers || [])
      .filter(u => u.parentCustomerId === customer?.id)
      .map(u => (u.email || '').trim())
      .filter(e => e && e.toLowerCase() !== primary.toLowerCase());
    // Dedupe (case-insensitive) while preserving order.
    const seen = new Set();
    setChaseCc(siblingCcs.filter(e => {
      const k = e.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }));
    setChaseManual('');
    setChaseSaveDefault(false);
    setChaseOpen(true);
  };

  const chaseRecipientsOk = () => {
    if (chaseTo.length === 0) { toast.error('Add at least one recipient'); return false; }
    if (chaseTo.length > 1) { toast.error('Pick one primary recipient (extras go in Cc)'); return false; }
    return true;
  };

  // Shared by the quick Send button and the composer. `emails` = per-invoice
  // edits keyed by invoice uid (omitted → standard chase wording).
  const sendChase = async (emails) => {
    if (chasing || !chaseRecipientsOk()) return;
    setChasing(true);
    const force = !!chaseThrottle; // warning showing → admin is explicitly re-sending
    try {
      const res = await chaseCustomerOverdue(customerId, {
        force, to: chaseTo[0], cc: chaseCc, saveDefault: chaseSaveDefault, emails,
      });
      toast.success(
        res.count === 1
          ? `Reminder sent to ${res.sentTo}`
          : `${res.count} reminders sent to ${res.sentTo}${res.cc?.length ? ` +${res.cc.length} cc` : ''}`
      );
      if (res.failed?.length) toast.error(`${res.failed.length} email(s) failed: ${res.failed.map(f => f.invoice).join(', ')}`);
      setCustomer((c) => c ? {
        ...c,
        email: chaseSaveDefault ? chaseTo[0] : c.email,
        lastOverdueChaseAt: new Date().toISOString(),
        lastOverdueChaseTotal: res.total,
        lastOverdueChaseCount: res.count,
      } : c);
      setChaseComposerOpen(false);
      setChaseOpen(false);
      setChaseThrottle(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === 'recently-chased') {
        // In-app warning (native confirm is suppressed by Radix on mobile Safari).
        setChaseThrottle(d);
      } else {
        toast.error(typeof d === 'string' ? d : (d?.message || 'Chase failed — your edits are kept'));
      }
    } finally {
      setChasing(false);
    }
  };

  // Outstanding / overdue totals (computed from local invoices which mirror
  // Xero invoice status). Draft invoices are excluded — they haven't gone
  // to the customer yet.
  const { balances, overdueList } = useMemo(() => {
    let outstanding = 0;
    let overdue = 0;
    const overdueInvs = [];
    (invoices || []).forEach((inv) => {
      const s = (inv.status || '').toString();
      const xs = (inv.xeroStatus || '').toString();
      const paid = s === 'paid' || s === 'voided' || s === 'deleted'
        || xs === 'PAID' || xs === 'VOIDED' || xs === 'DELETED';
      const open = !paid && (
        ['sent', 'authorised', 'submitted', 'overdue', 'partially-paid'].includes(s)
        || ['AUTHORISED', 'SUBMITTED'].includes(xs)
      );
      if (!open) return;
      const amt = Number(inv.amount || 0);
      outstanding += amt;
      const d = inv.dueDate ? new Date(inv.dueDate) : null;
      if (d && !isNaN(d.getTime()) && d.getTime() < Date.now()) {
        overdue += amt;
        overdueInvs.push(inv);
      }
    });
    return {
      balances: { outstanding, overdue },
      overdueList: overdueInvs.sort((a, b) => {
        const da = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const db_ = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return da - db_; // oldest-first
      }),
    };
  }, [invoices]);

  const openAdd = () => {
    setSiteForm({ name: '', address: '', addressParts: null, lat: null, lng: null, geofenceMeters: 250, isLutromHQ: false });
    setAddOpen(true);
  };

  const saveSite = async () => {
    if (!siteForm.name.trim()) { toast.error('Site name is required'); return; }
    setSavingSite(true);
    try {
      const s = await createSite({
        name: siteForm.name.trim(),
        address: hasAddress(siteForm.addressParts) ? formatAddress(siteForm.addressParts, ', ') : siteForm.address.trim(),
        ...(hasAddress(siteForm.addressParts) ? { addressParts: siteForm.addressParts } : {}),
        customerId,
        lat: siteForm.lat == null ? null : Number(siteForm.lat),
        lng: siteForm.lng == null ? null : Number(siteForm.lng),
        geofenceMeters: Number(siteForm.geofenceMeters) || 250,
        isLutromHQ: !!siteForm.isLutromHQ,
      });
      toast.success(`Site "${s.name}" created${s.isLutromHQ ? ' and set as Lutrom HQ' : ''}`);
      setAddOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create site');
    } finally {
      setSavingSite(false);
    }
  };

  const openEditCustomer = () => {
    if (!customer) return;
    setCustomerForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company: customer.company || '',
      title: customer.title || '',
      role: customer.role || 'customer',
      xeroContactId: customer.xeroContactId || '',
      customerType: customer.customerType || 'business',
      notes: customer.notes || '',
      address: customer.address || '',
      addressParts: customer.addressParts || null,
      addressTouched: false,
      website: customer.website || '',
      clearXeroEmail: false,
    });
    setEditCustomerOpen(true);
  };

  // Only offer "clear in Xero" when an existing email is being removed from a linked contact.
  const showClearXeroEmail = !!(customer?.email && customer?.xeroContactId && !customerForm.email.trim());

  const saveCustomer = async () => {
    const isBusiness = customerForm.customerType !== 'private';
    if (isBusiness && !customerForm.company.trim()) {
      toast.error('Company name is required for a business');
      return;
    }
    if (!isBusiness && !customerForm.name.trim()) {
      toast.error('Name is required for a private customer');
      return;
    }
    const companyEmail = customerForm.email.trim();
    if (companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail)) {
      toast.error('Enter a valid email or leave it blank');
      return;
    }
    setSavingCustomer(true);
    try {
      // For a business the top-level record uses `company` for the id;
      // for a private customer it uses `name` (the person's full name)
      // and `company` is left blank.
      const nameField = isBusiness
        ? customerForm.company.trim()
        : customerForm.name.trim();
      const updated = await updateUser(customerId, {
        name: nameField,
        email: companyEmail,
        ...(showClearXeroEmail && customerForm.clearXeroEmail ? { clearXeroEmail: true } : {}),
        phone: customerForm.phone.trim(),
        company: isBusiness ? customerForm.company.trim() : '',
        title: customerForm.title.trim(),
        role: customerForm.role,
        xeroContactId: customerForm.xeroContactId || '',
        customerType: customerForm.customerType || 'business',
        notes: customerForm.notes || '',
        address: customerForm.addressTouched && hasAddress(customerForm.addressParts)
          ? formatAddress(customerForm.addressParts) : (customerForm.address || ''),
        ...(customerForm.addressTouched && hasAddress(customerForm.addressParts)
          ? { addressParts: customerForm.addressParts } : {}),
        website: customerForm.website || '',
      });
      setCustomer(updated);
      toast.success('Customer updated');
      if (updated.xeroAddressSync?.status === 'failed') {
        toast.error(`Address saved in Lutrom, but Xero wasn't updated: ${updated.xeroAddressSync.error}`, { duration: 10000 });
      } else if (updated.xeroAddressSync?.status === 'ok') {
        toast.success('Billing address updated in Xero');
      }
      if (updated.xeroEmailClear?.status === 'failed') {
        toast.error(`Email cleared in Lutrom, but Xero wasn't updated: ${updated.xeroEmailClear.error}`, { duration: 10000 });
      } else if (updated.xeroEmailClear?.status === 'ok') {
        toast.success('Email cleared on the Xero contact');
      }
      setEditCustomerOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingCustomer(false);
    }
  };


  const openEditSite = (s) => {
    setEditingSite(s);
    setEditSiteForm({
      name: s.name || '',
      address: s.address || '',
      addressParts: s.addressParts || null,
      lat: s.lat ?? null,
      lng: s.lng ?? null,
      geofenceMeters: s.geofenceMeters || 250,
      isLutromHQ: !!s.isLutromHQ,
    });
    setEditSiteOpen(true);
  };

  const saveEditSite = async () => {
    if (!editSiteForm.name.trim()) { toast.error('Site name is required'); return; }
    setSavingEditSite(true);
    try {
      await updateSite(editingSite.id, {
        name: editSiteForm.name.trim(),
        address: hasAddress(editSiteForm.addressParts) ? formatAddress(editSiteForm.addressParts, ', ') : editSiteForm.address.trim(),
        ...(hasAddress(editSiteForm.addressParts) ? { addressParts: editSiteForm.addressParts } : {}),
        lat: editSiteForm.lat == null ? null : Number(editSiteForm.lat),
        lng: editSiteForm.lng == null ? null : Number(editSiteForm.lng),
        geofenceMeters: Number(editSiteForm.geofenceMeters) || 250,
        isLutromHQ: !!editSiteForm.isLutromHQ,
      });
      toast.success('Site updated');
      setEditSiteOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingEditSite(false);
    }
  };

  const removeSite = async (s) => {
    if (!window.confirm(`Delete site "${s.name}"? This cannot be undone. Assets at this site must be removed first.`)) return;
    try {
      await deleteSite(s.id);
      toast.success(`Site "${s.name}" deleted`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return <div className="p-6 text-slate-500" data-testid="customer-detail-loading">Loading...</div>;
  }

  if (!customer) {
    return (
      <div className="p-4 sm:p-6 lg:p-8" data-testid="customer-not-found">
        <Button variant="outline" onClick={() => navigate('/admin/customers')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to customers
        </Button>
        <div className="mt-6 bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">Customer not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="customer-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin/customers')} className="mb-1 -ml-2 text-slate-600">
        <ArrowLeft className="h-4 w-4 mr-2" /> All customers
      </Button>

      <XeroAddressSyncBanner
        kind="customer"
        doc={customer}
        onDone={(r) => setCustomer((c) => ({ ...c, xeroAddressSync: r.status === 'skipped' ? c.xeroAddressSync : r }))}
      />

      <PageHeader
        title={customer.company || customer.name}
        description={customer.company && customer.name && customer.company !== customer.name ? customer.name : 'Customer profile'}
        actions={
          <div className="flex flex-wrap gap-2">
            {/* Impersonation. Sub-customer accounts often share a parent
                portal, so we only show this for top-level customers —
                the ones that actually own a portal session. Admin can
                still open the sub-customer's parent to view its portal. */}
            {!customer.parentCustomerId && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await startImpersonation(customer.id);
                    navigate('/customer');
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Could not open customer portal');
                  }
                }}
                data-testid="view-as-customer-btn"
                title="Open the customer portal signed in as this customer"
              >
                <UserCog className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">View portal as customer</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={openEditCustomer}
              data-testid="edit-customer-btn"
            >
              <Pencil className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Edit details</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMergeOpen(true)}
              data-testid="merge-customer-btn"
              title="Merge this customer into another customer record"
            >
              <GitMerge className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Merge</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                // Light guard — these objects often have years of data
                // hanging off them (sites, assets, WOs, invoices), so the
                // confirm copy spells it out before we hit the API.
                if (!window.confirm(`Delete ${customer.name}? This cannot be undone.`)) return;
                try {
                  await deleteUser(customer.id);
                  toast.success('Customer deleted');
                  navigate('/admin/customers');
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Delete failed');
                }
              }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              data-testid="delete-customer-btn"
            >
              <Trash2 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
          </div>
        }
      />

      {customer.parentCustomerId && (
        (() => {
          const parent = allCustomers.find(c => c.id === customer.parentCustomerId);
          const parentName = parent ? (parent.company || parent.name) : null;
          return (
            <div
              className="bg-indigo-50 border border-indigo-200 rounded-md p-3 flex items-center gap-3 text-sm"
              data-testid="sub-customer-banner"
            >
              <GitBranch className="h-4 w-4 text-indigo-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-indigo-900">
                  Sub-customer of {parentName || 'a parent customer'}
                </div>
                <div className="text-xs text-indigo-800/80">
                  Sites and assets here belong to this sub-customer. Work orders and invoices
                  are billed to <strong>{parentName || 'the parent'}</strong>.
                </div>
              </div>
              {parent && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/admin/customers/${parent.id}`)}
                  data-testid="open-parent-customer"
                >
                  Open parent
                </Button>
              )}
            </div>
          );
        })()
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
        <InfoCard
          label="Open work orders"
          value={orders.filter(o => o.status !== 'completed').length}
          icon={ClipboardList}
          testId="customer-open-wos"
          onClick={() => navigate(`/admin/work-orders?customerId=${customerId}&status=open`)}
        />
        <InfoCard
          label="Outstanding work orders"
          value={orders.filter(o => o.status === 'completed' && o.needsAdminReview).length}
          icon={ShieldAlert}
          testId="customer-outstanding-wos"
          onClick={() => navigate(`/admin/work-orders?customerId=${customerId}&status=outstanding`)}
        />
        <InfoCard
          label="Closed work orders"
          value={orders.filter(o => o.status === 'completed' && !o.needsAdminReview).length}
          icon={CheckCircle2}
          testId="customer-closed-wos"
          onClick={() => navigate(`/admin/work-orders?customerId=${customerId}&status=closed`)}
        />
        <InfoCard
          label="Outstanding invoices"
          value={`£${Number(balances.outstanding || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          icon={Wallet}
          testId="customer-outstanding"
          onClick={() => navigate(`/admin/invoices?customerId=${customerId}&status=outstanding`)}
        />
        <InfoCard
          label="Overdue invoices"
          value={`£${Number(balances.overdue || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          icon={AlertCircle}
          testId="customer-overdue"
          valueClassName={balances.overdue > 0 ? 'text-red-600' : 'text-slate-400'}
          iconClassName={balances.overdue > 0 ? 'text-red-600' : 'text-slate-700'}
          onClick={() => navigate(`/admin/invoices?customerId=${customerId}&status=overdue`)}
        />
      </div>

      {/* Chase overdue banner — only when there's something to chase. Shows
          the last-sent stamp so admins don't spam the customer. */}
      {balances.overdue > 0 && (
        <div
          data-testid="chase-overdue-banner"
          className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold text-red-900 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {overdueList.length} invoice{overdueList.length === 1 ? '' : 's'} overdue — total £{Number(balances.overdue).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            {customer.lastOverdueChaseAt && (
              <div className="text-xs text-red-700 mt-0.5">
                Last chased {new Date(customer.lastOverdueChaseAt).toLocaleString('en-GB')}
              </div>
            )}
          </div>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={openChaseDialog}
            disabled={chaseContacts.length === 0}
            data-testid="chase-overdue-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Chase overdue
          </Button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="font-display uppercase tracking-wide text-sm text-slate-700 mb-3">Contact</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {customer.company && customer.name && customer.company !== customer.name && (
            <div className="flex items-center gap-2 text-slate-700">
              <Building2 className="h-4 w-4 text-slate-400" /> {customer.name}
            </div>
          )}
          {customer.email && (
            <div className="flex items-center gap-2 text-slate-700">
              <Mail className="h-4 w-4 text-slate-400" /> {customer.email}
            </div>
          )}
          {customer.phone && (
            <div className="flex items-center gap-2 text-slate-700">
              <Phone className="h-4 w-4 text-slate-400" /> {customer.phone}
            </div>
          )}
          {!customer.email && (
            <div className="flex items-center gap-2 text-slate-400 italic" data-testid="customer-no-company-email">
              <Mail className="h-4 w-4" /> No general company email — see Business employees &amp; contacts below
            </div>
          )}
        </div>
      </div>

      {/* Per-customer payment terms — drives auto-billed invoices' due
          dates AND queues an early-payment discount as a credit on the
          customer's NEXT invoice when they pay within a tier window. */}
      <CustomerPaymentTermsCard customerId={customer.id} />

      {/* Admin's view of the "How are we doing at your sites?" block —
          same widget the customer sees on their portal, scoped to this
          customer's data (and their sub-customers). Only rendered for
          top-level customers; sub-customer detail pages already show
          the parent's block via the parent chain. */}
      {!customer.parentCustomerId && (
        <SitePerformance variant="customer" customerId={customer.id} />
      )}

      {/* Lutrom team section removed per user request — customer-side
          contacts already appear in the Contact box above, and the
          internal Lutrom roster is managed on /admin/users. */}

      <CustomerContactsCard
        customer={customer}
        onSaved={(updated) => setCustomer(updated)}
      />

      {/* Portal-access controls — the single place an admin manages this
          customer's ability to sign in. Surfaces invite status, exposes
          resend/copy-invite for pending accounts and a one-click
          "send password reset" for claimed accounts (used by support
          when the customer says "I can't log in" over the phone). */}
      <PortalAccessCard
        customer={customer}
        onSaved={(updated) => setCustomer(updated)}
      />

      <SubCustomersCard
        parent={customer}
        allCustomers={allCustomers}
        onReload={reload}
      />

      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase tracking-wide text-base text-slate-700">Sites</h2>
        <Button
          size="sm"
          onClick={openAdd}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="add-site-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Add site
        </Button>
      </div>

      {sitesEnriched.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <MapPin className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No sites yet</div>
          <div className="text-sm text-slate-500 mb-4">Add a site to start tracking assets and work orders.</div>
          <Button
            onClick={openAdd}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="add-first-site-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add first site
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sitesEnriched.map(s => (
            <Link
              key={s.id}
              to={`/admin/customers/${s.customerId || customerId}/sites/${s.id}`}
              data-testid={`site-card-${s.id}`}
              className="group bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm rounded-md p-5 transition-all"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="h-10 w-10 rounded bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="font-semibold text-slate-900 truncate group-hover:text-blue-700 transition-colors">{s.name}</div>
                    {s.isLutromHQ && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200"
                        data-testid={`site-hq-badge-${s.id}`}
                        title="Lutrom HQ — used to geofence the Lutrom clock-in lane"
                      >
                        <Home className="h-2.5 w-2.5" /> HQ
                      </span>
                    )}
                    {s.viaSubCustomerName && (
                      <span
                        className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                        data-testid={`site-sub-badge-${s.id}`}
                        title={`Site lives under sub-customer ${s.viaSubCustomerName}`}
                      >
                        via {s.viaSubCustomerName}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{s.address || 'No address on file'}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900 transition-colors shrink-0" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                <div>
                  <div className="text-2xl font-display tracking-tight text-slate-900">{s.assetCount}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Boxes className="h-3 w-3" /> {s.assetCount === 1 ? 'asset' : 'assets'}
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-display tracking-tight text-slate-900">{s.openWorkOrders}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <ClipboardList className="h-3 w-3" /> open WOs
                  </div>
                </div>
              </div>

              <div
                className="flex items-center gap-1.5 text-xs mt-3 pt-3 border-t border-slate-100"
                data-testid={`site-geofence-${s.id}`}
              >
                <Crosshair
                  className={`h-3.5 w-3.5 ${typeof s.lat === 'number' && typeof s.lng === 'number' ? 'text-emerald-600' : 'text-slate-400'}`}
                />
                {typeof s.lat === 'number' && typeof s.lng === 'number' ? (
                  <span className="text-slate-700">
                    <span className="font-mono">{s.lat.toFixed(4)}, {s.lng.toFixed(4)}</span>
                    <span className="text-slate-500 ml-1.5">· {s.geofenceMeters || 250}m</span>
                  </span>
                ) : (
                  <span className="text-amber-700">No coordinates — clock-in unrestricted</span>
                )}
              </div>

              <div
                className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-slate-100"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEditSite(s)}
                  data-testid={`edit-site-${s.id}`}
                  title="Edit site details"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setSafetyDocSite(s); setSafetyDocOpen(true); }}
                  data-testid={`safety-doc-${s.id}`}
                  title="Edit the site safety doc that feeds the AI RAMS generator"
                  className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                >
                  <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Safety
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeSite(s)}
                  data-testid={`delete-site-${s.id}`}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  title={s.assetCount > 0 ? 'Remove assets first to delete this site' : 'Delete site'}
                  disabled={s.assetCount > 0}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Stores section — parallel twin to Sites. Admin can see every store
          scoped to this customer; a read-only list here because store CRUD
          already lives on the dedicated /admin/stores page. */}
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase tracking-wide text-base text-slate-700">Stores</h2>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/admin/stores')}
          data-testid="manage-stores-btn"
        >
          <Warehouse className="h-4 w-4 mr-2" /> Manage stores
        </Button>
      </div>

      {stores.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <Warehouse className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No stores yet</div>
          <div className="text-sm text-slate-500 mb-4">This customer can still pull spare parts from your central stores, but nothing is pinned to their site yet.</div>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/stores')}
            data-testid="add-first-store-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add a store
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stores.map(st => (
            <Link
              key={st.id}
              to={`/admin/stores/${st.id}`}
              data-testid={`store-card-${st.id}`}
              className="group bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm rounded-md p-5 transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                  <Warehouse className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate group-hover:text-blue-700 transition-colors">{st.name}</div>
                  <div className="text-xs text-slate-500 truncate">{st.address || st.description || 'Central store'}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900 transition-colors shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* ───────── Effective Rate Card ─────────
          At-a-glance pricing across all 8 bands. Resolved server-side via
          the same matcher the work-order billing uses, so what you see here
          is exactly what an engineer's hours would be billed at. Badges
          signal whether a rule is customer-specific or falling back to the
          default wildcard. Section is read-only — admin clicks through to
          /admin/rate-cards to actually edit. */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="font-display uppercase tracking-wide text-base text-slate-700">Rate Card</h2>
          {rateCard?.hasCustomerSpecificRules && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
              data-testid="rate-card-customer-specific-badge"
            >
              <CheckCircle2 className="h-3 w-3" /> Custom pricing
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/admin/rate-cards')}
          data-testid="manage-rate-cards-btn"
        >
          <PoundSterling className="h-4 w-4 mr-2" /> Manage rate cards
        </Button>
      </div>

      {rateCard ? (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="rate-card-summary">
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            {rateCard.bands.map((b) => (
              <div key={b.band} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`rate-card-band-${b.band}`}>
                <div className="min-w-0">
                  <div className="text-sm text-slate-700">{b.label}</div>
                  {b.source === 'none' ? (
                    <div className="text-[11px] text-amber-700 flex items-center gap-1 mt-0.5">
                      <AlertCircle className="h-3 w-3" /> No rule — needs admin review
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {b.source === 'customer-specific' ? 'Customer-specific' : 'House default'}
                      {b.rateName ? ` · ${b.rateName}` : ''}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  {b.rate != null ? (
                    <span className="text-base font-semibold text-slate-900 tabular-nums">£{b.rate.toFixed(2)}<span className="text-xs font-normal text-slate-500">/h</span></span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">
          Loading rate card…
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-site-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add site for {customer.name}</DialogTitle>
            <DialogDescription>
              Sites group the assets at one physical location. You can add as many sites as you like per customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Site name</Label>
              <Input
                data-testid="site-name-input"
                className="mt-1.5"
                placeholder="e.g. Manchester Plant"
                value={siteForm.name}
                onChange={e => setSiteForm({ ...siteForm, name: e.target.value })}
                autoFocus
              />
            </div>
            <div>
              <Label>Address <span className="text-xs text-slate-500 font-normal">(optional)</span></Label>
              <div className="mt-1.5">
                <UkAddressFields
                  value={siteForm.addressParts}
                  legacyText={siteForm.address}
                  onChange={(parts, picked) => setSiteForm((f) => ({
                    ...f,
                    addressParts: parts,
                    // GPS only pre-fills a new site with no pin yet; never overwrites coordinates.
                    lat: f.lat == null && picked?.lat != null ? picked.lat : f.lat,
                    lng: f.lng == null && picked?.lng != null ? picked.lng : f.lng,
                  }))}
                  testId="site-address-input"
                />
              </div>
            </div>
            <SiteGeofenceFields
              form={siteForm}
              setForm={setSiteForm}
              testIdPrefix="add-site"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={savingSite}>Cancel</Button>
            <Button
              onClick={saveSite}
              disabled={savingSite}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-site-btn"
            >
              {savingSite ? 'Saving...' : 'Save site'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit customer dialog */}
      <Dialog open={editCustomerOpen} onOpenChange={setEditCustomerOpen}>
        <DialogContent data-testid="edit-customer-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit customer details</DialogTitle>
            <DialogDescription>
              Company-level info (address, phone, general email, website).
              Individual people at this customer are managed in the <em>Business employees &amp; contacts</em> card below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Customer type</Label>
              <Select
                value={customerForm.customerType || 'business'}
                onValueChange={(v) => setCustomerForm({ ...customerForm, customerType: v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="edit-customer-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="private">Private (person)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">
                {(customerForm.customerType || 'business') === 'private'
                  ? 'Private: sites are optional and a single contact is usually enough.'
                  : 'Business: full multi-site + multi-contact support.'}
              </div>
            </div>
            {(customerForm.customerType || 'business') === 'business' ? (
              <div className="md:col-span-2">
                <Label>Company *</Label>
                <Input
                  data-testid="edit-customer-company"
                  className="mt-1.5"
                  value={customerForm.company}
                  onChange={e => setCustomerForm({ ...customerForm, company: e.target.value })}
                  autoFocus
                />
              </div>
            ) : (
              <div className="md:col-span-2">
                <Label>Full name *</Label>
                <Input
                  data-testid="edit-customer-name"
                  className="mt-1.5"
                  value={customerForm.name}
                  onChange={e => setCustomerForm({ ...customerForm, name: e.target.value })}
                  autoFocus
                />
              </div>
            )}
            <div>
              <Label>{(customerForm.customerType || 'business') === 'business' ? 'General company email' : 'Email'} <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                data-testid="edit-customer-email"
                type="email"
                className="mt-1.5"
                value={customerForm.email}
                placeholder="e.g. info@company.co.uk"
                onChange={e => setCustomerForm({ ...customerForm, email: e.target.value })}
              />
              <p className="text-[11px] text-slate-500 mt-1">Leave blank if there&rsquo;s no shared mailbox — add people &amp; departments under contacts.</p>
              {showClearXeroEmail && (
                <label className="flex items-start gap-2 mt-2 text-[11px] text-slate-600 cursor-pointer" data-testid="clear-xero-email-label">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!customerForm.clearXeroEmail}
                    onChange={e => setCustomerForm({ ...customerForm, clearXeroEmail: e.target.checked })}
                    data-testid="clear-xero-email-checkbox"
                  />
                  Also clear the email on the linked Xero contact (otherwise Xero keeps its current email)
                </label>
              )}
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                data-testid="edit-customer-phone"
                className="mt-1.5"
                value={customerForm.phone}
                onChange={e => setCustomerForm({ ...customerForm, phone: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <div className="mt-1.5">
                <UkAddressFields
                  value={customerForm.addressParts}
                  legacyText={customerForm.address}
                  onChange={(parts) => setCustomerForm((f) => ({ ...f, addressParts: parts, addressTouched: true }))}
                  testId="edit-customer-address"
                />
                <p className="text-[11px] text-slate-500 mt-1">Billing address. Saving a changed address also updates the linked Xero contact.</p>
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Website</Label>
              <Input
                data-testid="edit-customer-website"
                className="mt-1.5"
                value={customerForm.website}
                onChange={e => setCustomerForm({ ...customerForm, website: e.target.value })}
                placeholder="https://www.example.com"
                inputMode="url"
                autoComplete="off"
              />
            </div>
            <div className="md:col-span-1">
              <Label className="flex items-center justify-between">
                <span>Xero contact</span>
                {!xeroConnected && (
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-normal">Xero not connected</span>
                )}
              </Label>
              <Select
                value={customerForm.xeroContactId || 'none'}
                onValueChange={(v) => setCustomerForm({ ...customerForm, xeroContactId: v === 'none' ? '' : v })}
                disabled={!xeroConnected || loadingContacts}
              >
                <SelectTrigger className="mt-1.5" data-testid="edit-customer-xero">
                  <SelectValue placeholder={loadingContacts ? 'Loading contacts...' : 'Not linked'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">— Not linked —</SelectItem>
                  {xeroContacts.map(c => (
                    <SelectItem key={c.xeroContactId} value={c.xeroContactId}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Internal notes (admin-only)</Label>
              <textarea
                value={customerForm.notes || ''}
                onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                rows={3}
                placeholder="Birthdays, allergies, gate codes, preferred contact time, anything else you want to remember. Never shown to the customer."
                className="w-full mt-1.5 border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="edit-customer-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCustomerOpen(false)} disabled={savingCustomer}>Cancel</Button>
            <Button
              onClick={saveCustomer}
              disabled={savingCustomer}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-customer-btn"
            >
              {savingCustomer ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit site dialog */}
      <Dialog open={editSiteOpen} onOpenChange={setEditSiteOpen}>
        <DialogContent data-testid="edit-site-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit site{editingSite ? ` — ${editingSite.name}` : ''}</DialogTitle>
            <DialogDescription>
              Rename the site or update its address. Assets and work orders linked to this site will keep their references.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Site name</Label>
              <Input
                data-testid="edit-site-name"
                className="mt-1.5"
                value={editSiteForm.name}
                onChange={e => setEditSiteForm({ ...editSiteForm, name: e.target.value })}
                autoFocus
              />
            </div>
            <div>
              <Label>Address <span className="text-xs text-slate-500 font-normal">(optional)</span></Label>
              <div className="mt-1.5">
                <UkAddressFields
                  value={editSiteForm.addressParts}
                  legacyText={editSiteForm.address}
                  onChange={(parts, picked) => setEditSiteForm((f) => ({
                    ...f,
                    addressParts: parts,
                    lat: f.lat == null && picked?.lat != null ? picked.lat : f.lat,
                    lng: f.lng == null && picked?.lng != null ? picked.lng : f.lng,
                  }))}
                  testId="edit-site-address"
                />
              </div>
            </div>
            <SiteGeofenceFields
              form={editSiteForm}
              setForm={setEditSiteForm}
              testIdPrefix="edit-site"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSiteOpen(false)} disabled={savingEditSite}>Cancel</Button>
            <Button
              onClick={saveEditSite}
              disabled={savingEditSite}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-edit-site-btn"
            >
              {savingEditSite ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chase overdue confirmation dialog */}
      <Dialog
        open={chaseOpen}
        onOpenChange={(o) => {
          if (chasing) return;
          setChaseOpen(o);
          if (!o) setChaseThrottle(null);
        }}
      >
        <DialogContent data-testid="chase-overdue-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Chase overdue invoices</DialogTitle>
            <DialogDescription>
              One email per invoice — each with the original invoice PDF attached
              and a &ldquo;Review and pay&rdquo; link. Pick the recipient(s) below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {chaseThrottle && (
              <div
                className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900"
                data-testid="chase-throttle-warning"
              >
                <div className="font-semibold mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" /> Recently chased
                </div>
                <div>{chaseThrottle.message}</div>
                {chaseThrottle.lastChasedCount != null && (
                  <div className="text-xs mt-1 text-amber-800">
                    Previous send: {chaseThrottle.lastChasedCount} invoice{chaseThrottle.lastChasedCount === 1 ? '' : 's'}, £{Number(chaseThrottle.lastChasedTotal || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                )}
              </div>
            )}

            {/* Recipient picker — TO / CC chip stacks + free-text add +
                per-contact To/Cc buttons. Mirrors the invoice email
                dialog on AdminInvoiceDetail so the two flows feel the
                same. */}
            <div className="space-y-2">
              <RecipientStack
                label="To"
                emails={chaseTo}
                onRemove={removeChaseEmail}
                accent="emerald"
                testid="chase-to"
              />
              <RecipientStack
                label="Cc"
                emails={chaseCc}
                onRemove={removeChaseEmail}
                accent="amber"
                testid="chase-cc"
              />
            </div>

            <div>
              <Label htmlFor="chase-manual">Add an email</Label>
              <div className="flex gap-1.5 mt-1.5">
                <Input
                  id="chase-manual"
                  type="email"
                  value={chaseManual}
                  onChange={(e) => setChaseManual(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addChaseManual('to');
                    }
                  }}
                  placeholder="name@company.com"
                  className="flex-1 min-w-0"
                  data-testid="chase-manual"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addChaseManual('to')}
                  disabled={!chaseManual.trim()}
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 px-3"
                  data-testid="chase-manual-to"
                >
                  → To
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addChaseManual('cc')}
                  disabled={!chaseManual.trim()}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50 px-3"
                  data-testid="chase-manual-cc"
                >
                  → Cc
                </Button>
              </div>
            </div>

            {chaseContacts.length > 0 && (
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Customer contacts
                </div>
                <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                  {chaseContacts.map((c) => {
                    const bucket = chaseBucket(c.email);
                    return (
                      <div
                        key={c.id}
                        className="flex items-center gap-2 px-3 py-2"
                        data-testid={`chase-contact-${c.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900 truncate">
                            {c.isPrimary && (
                              <span className="text-[9px] uppercase tracking-wider font-semibold px-1 py-px rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Primary
                              </span>
                            )}
                            {!c.isPrimary && c.source === 'customer' && (
                              <span
                                className="text-[9px] uppercase tracking-wider font-semibold px-1 py-px rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                                title="Sub-customer under the primary payer"
                              >
                                Sub
                              </span>
                            )}
                            {c.source === 'contact' && (
                              <span
                                className="text-[9px] uppercase tracking-wider font-semibold px-1 py-px rounded bg-slate-100 text-slate-700 border border-slate-200"
                                title="Team contact stored on the customer record"
                              >
                                Contact
                              </span>
                            )}
                            <span className="truncate">{c.name || c.email}</span>
                          </div>
                          {c.name && (
                            <div className="text-xs text-slate-500 truncate">{c.email}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => assignChaseEmail(c.email, 'to')}
                          className={
                            'px-2.5 py-1 rounded-md border text-xs font-medium transition-colors '
                            + (bucket === 'to'
                              ? 'bg-emerald-700 border-emerald-700 text-white'
                              : 'border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-800')
                          }
                          data-testid={`chase-contact-${c.id}-to`}
                        >
                          To
                        </button>
                        <button
                          type="button"
                          onClick={() => assignChaseEmail(c.email, 'cc')}
                          className={
                            'px-2.5 py-1 rounded-md border text-xs font-medium transition-colors '
                            + (bucket === 'cc'
                              ? 'bg-amber-600 border-amber-600 text-white'
                              : 'border-slate-300 text-slate-600 hover:border-amber-500 hover:text-amber-800')
                          }
                          data-testid={`chase-contact-${c.id}-cc`}
                        >
                          Cc
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Save as customer's default — only makes sense when the
                admin picked a single TO that isn't already the stored
                primary email. Hidden otherwise to avoid confusing UX. */}
            {chaseTo.length === 1
              && chaseTo[0].toLowerCase() !== (customer.email || '').toLowerCase() && (
              <label
                className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none"
                data-testid="chase-save-default-label"
              >
                <Checkbox
                  checked={chaseSaveDefault}
                  onCheckedChange={(v) => setChaseSaveDefault(!!v)}
                  data-testid="chase-save-default"
                />
                <span>
                  Save <span className="font-medium">{chaseTo[0]}</span> as this customer&rsquo;s default email
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Future invoice sends and chases will default to this address.
                  </span>
                </span>
              </label>
            )}

            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-red-700">Total overdue</div>
              <div className="text-2xl font-display text-red-800">
                £{Number(balances.overdue).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-red-700 mt-0.5">
                across {overdueList.length} invoice{overdueList.length === 1 ? '' : 's'}
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Invoice</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Due</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueList.map((inv) => {
                    const due = inv.dueDate ? new Date(inv.dueDate) : null;
                    const daysPast = due ? Math.max(0, Math.floor((Date.now() - due.getTime()) / 86400000)) : 0;
                    return (
                      <tr key={inv.id} className="border-t border-slate-100" data-testid={`chase-row-${inv.id}`}>
                        <td className="px-3 py-2 font-mono text-slate-900">{inv.xeroInvoiceNumber || inv.id}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {due ? formatDate(due) : '—'}
                          <span className="text-red-600 ml-1.5">({daysPast}d)</span>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">
                          £{Number(inv.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setChaseOpen(false); setChaseThrottle(null); }} disabled={chasing}>Cancel</Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => { if (chaseRecipientsOk()) setChaseComposerOpen(true); }}
              disabled={chasing || chaseTo.length !== 1}
              className="border-slate-300 text-slate-800 hover:bg-slate-50"
              data-testid="chase-preview-edit-btn"
            >
              <Eye className="h-4 w-4 mr-2" /> Preview &amp; edit emails
            </Button>
            <Button
              onClick={() => sendChase()}
              disabled={chasing || chaseTo.length !== 1}
              className={chaseThrottle ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="confirm-chase-btn"
            >
              {chasing ? 'Sending...' : (
                <><Send className="h-4 w-4 mr-2" /> {chaseThrottle ? 'Send anyway' : 'Send chase email'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChaseEmailComposer
        open={chaseComposerOpen}
        onClose={() => setChaseComposerOpen(false)}
        customerId={customerId}
        to={chaseTo[0]}
        cc={chaseCc}
        throttle={chaseThrottle}
        sending={chasing}
        onSend={(emails) => sendChase(emails)}
      />

      {/* Merge-customers dialog — pick another customer record and fold
          this one into it (or vice versa). Every site/asset/WO/invoice
          pointing at the loser is rewritten; the loser row is deleted. */}
      <Dialog open={mergeOpen} onOpenChange={(o) => !merging && setMergeOpen(o)}>
        <DialogContent data-testid="merge-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Merge customer records</DialogTitle>
            <DialogDescription>
              Combines two customer profiles into one. Every site, asset, work order,
              invoice, store, team user and historical record pointing at the "from"
              record is moved over, then the "from" record is deleted. <strong>This cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Merge with</Label>
              <select
                className="mt-1.5 w-full border border-slate-200 rounded-md h-10 px-3 text-sm bg-white"
                value={mergeTargetId}
                onChange={(e) => setMergeTargetId(e.target.value)}
                data-testid="merge-target-select"
              >
                <option value="">— choose a customer —</option>
                {allCustomers
                  .filter((u) => !u.parentCustomerId)
                  .sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || ''))
                  .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.company || c.name}{c.email ? ` · ${c.email}` : ''}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Direction</Label>
              <div className="mt-1.5 space-y-2">
                <label className="flex items-start gap-2 p-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                  <input
                    type="radio"
                    name="mergeDir"
                    value="into-target"
                    checked={mergeDirection === 'into-target'}
                    onChange={() => setMergeDirection('into-target')}
                    className="mt-0.5"
                    data-testid="merge-dir-into-target"
                  />
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">Merge <span className="text-slate-600">this</span> → into the selected one</div>
                    <div className="text-xs text-slate-500">"{customer.company || customer.name}" gets deleted; the selected customer keeps everything.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                  <input
                    type="radio"
                    name="mergeDir"
                    value="from-target"
                    checked={mergeDirection === 'from-target'}
                    onChange={() => setMergeDirection('from-target')}
                    className="mt-0.5"
                    data-testid="merge-dir-from-target"
                  />
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">Merge the selected one → into <span className="text-slate-600">this</span></div>
                    <div className="text-xs text-slate-500">The selected customer gets deleted; "{customer.company || customer.name}" keeps everything.</div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)} disabled={merging}>Cancel</Button>
            <Button
              disabled={!mergeTargetId || merging}
              onClick={async () => {
                const winnerId = mergeDirection === 'into-target' ? mergeTargetId : customerId;
                const loserId  = mergeDirection === 'into-target' ? customerId   : mergeTargetId;
                if (!window.confirm(`Really merge these customers? The loser record will be permanently deleted.`)) return;
                setMerging(true);
                try {
                  const res = await mergeCustomers(winnerId, loserId);
                  const movedBits = Object.entries(res.summary || {})
                    .map(([k, v]) => `${v} ${k.split('.')[0]}`).join(', ') || 'no records';
                  toast.success(`Merged — ${movedBits} moved`);
                  setMergeOpen(false);
                  // If we were the loser we navigate to the winner; otherwise
                  // stay on this page and refresh the data.
                  if (loserId === customerId) {
                    navigate(`/admin/customers/${winnerId}`);
                  } else {
                    window.location.reload();
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Merge failed');
                } finally {
                  setMerging(false);
                }
              }}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="confirm-merge-btn"
            >
              {merging ? 'Merging...' : <><GitMerge className="h-4 w-4 mr-2" /> Merge</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site safety doc — feeds the AI RAMS generator. */}
      <SiteSafetyDocDialog
        site={safetyDocSite}
        open={safetyDocOpen}
        onOpenChange={setSafetyDocOpen}
        onSaved={refresh}
      />
    </div>
  );
};

const InfoCard = ({ label, value, icon: Icon, testId, valueClassName, iconClassName, onClick }) => {
  const body = (
    <>
      <div className="h-10 w-10 rounded bg-slate-100 flex items-center justify-center shrink-0">
        <Icon className={`h-5 w-5 ${iconClassName || 'text-slate-700'}`} />
      </div>
      <div className="min-w-0">
        <div className={`text-2xl font-display tracking-tight truncate ${valueClassName || 'text-slate-900'}`}>{value}</div>
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="bg-white border border-slate-200 rounded-md p-5 flex items-center gap-3 text-left hover:border-slate-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all w-full"
      >
        {body}
      </button>
    );
  }
  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-5 flex items-center gap-3"
      data-testid={testId}
    >
      {body}
    </div>
  );
};

/**
 * Reusable lat / lng / radius + map block for the Add and Edit Site
 * dialogs. The Leaflet picker writes lat/lng back via onChange; manual
 * inputs are exposed underneath for fine-tuning. Radius drives the
 * geofence — engineers more than this many metres from (lat, lng) cannot
 * clock in to a work order at this site.
 */
const SiteGeofenceFields = ({ form, setForm, testIdPrefix }) => (
  <div className="border-t border-slate-200 pt-4 mt-4">
    <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">
      Coordinates &amp; geofence
    </Label>
    <SiteMapPicker
      lat={form.lat}
      lng={form.lng}
      radiusMeters={form.geofenceMeters}
      onChange={({ lat, lng }) => setForm(f => ({ ...f, lat, lng }))}
    />
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
      <div>
        <Label className="text-[10px] text-slate-500">Latitude</Label>
        <Input
          type="number"
          step="0.000001"
          value={form.lat ?? ''}
          onChange={e => setForm(f => ({ ...f, lat: e.target.value === '' ? null : Number(e.target.value) }))}
          placeholder="53.483"
          className="mt-0.5"
          data-testid={`${testIdPrefix}-lat-input`}
        />
      </div>
      <div>
        <Label className="text-[10px] text-slate-500">Longitude</Label>
        <Input
          type="number"
          step="0.000001"
          value={form.lng ?? ''}
          onChange={e => setForm(f => ({ ...f, lng: e.target.value === '' ? null : Number(e.target.value) }))}
          placeholder="-2.244"
          className="mt-0.5"
          data-testid={`${testIdPrefix}-lng-input`}
        />
      </div>
      <div>
        <Label className="text-[10px] text-slate-500">Radius (m)</Label>
        <Input
          type="number"
          min="50"
          step="10"
          value={form.geofenceMeters}
          onChange={e => setForm(f => ({ ...f, geofenceMeters: Number(e.target.value) || 250 }))}
          className="mt-0.5"
          data-testid={`${testIdPrefix}-radius-input`}
        />
      </div>
    </div>
    <p className="text-xs text-slate-500 mt-2">
      Engineers more than this far from the coordinates can&rsquo;t clock in to work orders at this site.
      Default 250&nbsp;m covers most industrial sites with car parks.
    </p>

    <div className="mt-4 pt-4 border-t border-slate-200">
      <div className="flex items-start justify-between gap-4 rounded-md bg-slate-50 border border-slate-200 p-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
            <Home className="h-3.5 w-3.5 text-blue-700" />
            This is the Lutrom HQ site
          </div>
          <p className="text-xs text-slate-600 mt-1">
            Engineers clocking into the <span className="font-medium">Lutrom</span> lane (start/end of
            day, van pickup) must be within this site&rsquo;s geofence. Only one site can be HQ —
            enabling this un-sets every other site automatically.
          </p>
        </div>
        <Switch
          checked={!!form.isLutromHQ}
          onCheckedChange={(v) => setForm(f => ({ ...f, isLutromHQ: !!v }))}
          data-testid={`${testIdPrefix}-is-hq-switch`}
          className="shrink-0 mt-0.5"
        />
      </div>
    </div>
  </div>
);

export default CustomerDetail;

/**
 * Editable list of the customer's sub-contacts (e.g. site engineers,
 * project managers). Renders below the main customer edit card so the
 * admin can keep multiple email addresses per customer for use in the
 * EICR / Quote / Invoice email autocomplete.
 */
const CONTACT_DEPARTMENTS = ['Accounts', 'Purchasing', 'Sales', 'Operations'];

const CustomerContactsCard = ({ customer, onSaved }) => {
  const [rows, setRows] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  // Per-contact portal state (keyed by lowercased email). Populated
  // whenever the row list changes so the pills stay in sync with what's
  // actually in `users` — the admin might have already invited someone
  // from the Users page directly.
  const [portalByEmail, setPortalByEmail] = React.useState({});
  const [busyContact, setBusyContact] = React.useState(null);

  React.useEffect(() => {
    setRows(Array.isArray(customer?.contacts) ? customer.contacts : []);
    setDirty(false);
  }, [customer?.id, customer?.contacts]);

  const refreshPortalStatus = React.useCallback(async () => {
    if (!customer?.id) return;
    try {
      const { byEmail } = await getContactPortalStatus(customer.id);
      setPortalByEmail(byEmail || {});
    } catch {
      // Silent — the pills are non-critical; failing to load them
      // shouldn't block editing contacts.
      setPortalByEmail({});
    }
  }, [customer?.id]);

  React.useEffect(() => { refreshPortalStatus(); }, [refreshPortalStatus]);

  const patchRow = (i, field, value) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, [field]: value } : r)));
    setDirty(true);
  };
  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: (crypto?.randomUUID && crypto.randomUUID()) || `c-${Date.now()}`,
        name: '', email: '', phone: '', title: '', department: '' },
    ]);
    setDirty(true);
  };
  const removeRow = (i) => {
    setRows((prev) => prev.filter((_, j) => j !== i));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = rows
        .map((r) => ({
          id: r.id || `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: (r.name || '').trim(),
          email: (r.email || '').trim(),
          phone: (r.phone || '').trim(),
          title: (r.title || '').trim(),
          department: (r.department || '').trim(),
          notes: (r.notes || '').trim(),
        }))
        .filter((r) => r.name || r.email || r.phone || r.department);
      const bad = cleaned.find((r) => r.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email));
      if (bad) {
        toast.error(`Invalid email: ${bad.email}`);
        return;
      }
      const updated = await updateUser(customer.id, { contacts: cleaned });
      toast.success(cleaned.length === 1 ? 'Contact saved' : `${cleaned.length} contacts saved`);
      setRows(cleaned);
      setDirty(false);
      onSaved && onSaved(updated);
      refreshPortalStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendContactInvite = async (row) => {
    // Only unsaved-then-invited contacts are noise; require save first.
    if (dirty) {
      toast.error('Save your contact changes first, then send the invite.');
      return;
    }
    if (!row.email) return toast.error('Contact needs an email to receive an invite');
    if (!row.name) return toast.error('Contact needs a name so we know who to greet in the email');
    const key = row.id || row.email;
    setBusyContact(key);
    try {
      const res = await inviteCustomerContact(customer.id, key);
      // Copy the action URL to clipboard as a graceful fallback for
      // domains Resend refuses (very common on the free tier).
      if (res?.actionUrl) {
        try { await navigator.clipboard.writeText(res.actionUrl); } catch { /* ignore */ }
      }
      if (res?.status === 'reset') {
        toast.success(`Reset link sent to ${res.email} — link also copied to your clipboard.`);
      } else {
        toast.success(`Invite sent to ${res.email} — link also copied to your clipboard.`);
      }
      refreshPortalStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send invite');
    } finally {
      setBusyContact(null);
    }
  };

  if (!customer) return null;

  const portalPill = (email) => {
    const state = portalByEmail[(email || '').trim().toLowerCase()];
    if (!state) return null;
    if (state.role && state.role !== 'customer') {
      return <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded" title={`Already a ${state.role} account`}>{state.role}</span>;
    }
    if (state.inviteStatus === 'pending') {
      return <span className="text-[10px] uppercase tracking-widest bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded" data-testid="contact-portal-pending">Invite pending</span>;
    }
    return <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded" data-testid="contact-portal-active">Portal active</span>;
  };

  const buttonLabel = (email) => {
    const state = portalByEmail[(email || '').trim().toLowerCase()];
    if (!state) return 'Send portal invite';
    if (state.inviteStatus === 'pending') return 'Resend invite';
    return 'Send reset';
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="customer-contacts-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-display uppercase tracking-wide text-base text-slate-700">
            Business employees &amp; contacts
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            The people you deal with at this customer (owner, PM, site engineer, end-client
            you subcontract for). They&#39;ll appear in the email autocomplete when you
            send an EICR / quote / invoice from this customer. Department mailboxes
            (accounts@, purchasing@, sales@) can be stored as their own row with a Department.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="customer-contact-add">
            <Plus className="h-4 w-4 mr-1" /> Add contact
          </Button>
          {dirty && (
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="customer-contact-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
      </div>
      <datalist id="contact-department-options">
        {CONTACT_DEPARTMENTS.map((d) => <option key={d} value={d} />)}
      </datalist>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center">
          No extra contacts yet. Click <em>Add contact</em> above to store one.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="hidden md:grid gap-2 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] px-2 text-[10px] uppercase tracking-widest text-slate-400" data-testid="customer-contact-headers">
            <span>Name</span><span>Email</span><span>Phone</span><span>Job title</span><span>Department</span><span className="w-9" />
          </div>
          {rows.map((r, i) => {
            const rowKey = r.id || r.email || i;
            const canInvite = !!(r.email && r.name);
            const busy = busyContact === (r.id || r.email);
            return (
              <div
                key={rowKey}
                className="border border-slate-100 rounded-md p-2 space-y-2"
                data-testid={`customer-contact-row-${i}`}
              >
                <div className="grid gap-2 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-center">
                  <Input
                    value={r.name || ''}
                    onChange={(e) => patchRow(i, 'name', e.target.value)}
                    placeholder="Name or mailbox (e.g. Accounts)"
                    data-testid={`customer-contact-name-${i}`}
                  />
                  <Input
                    value={r.email || ''}
                    onChange={(e) => patchRow(i, 'email', e.target.value)}
                    placeholder="email@example.com"
                    inputMode="email"
                    autoComplete="off"
                    data-testid={`customer-contact-email-${i}`}
                  />
                  <Input
                    value={r.phone || ''}
                    onChange={(e) => patchRow(i, 'phone', e.target.value)}
                    placeholder="+44…"
                    inputMode="tel"
                    autoComplete="off"
                    data-testid={`customer-contact-phone-${i}`}
                  />
                  <Input
                    value={r.title || ''}
                    onChange={(e) => patchRow(i, 'title', e.target.value)}
                    placeholder="Job title"
                    data-testid={`customer-contact-title-${i}`}
                  />
                  <Input
                    value={r.department || ''}
                    onChange={(e) => patchRow(i, 'department', e.target.value)}
                    placeholder="Department"
                    list="contact-department-options"
                    autoComplete="off"
                    data-testid={`customer-contact-department-${i}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRow(i)}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    data-testid={`customer-contact-remove-${i}`}
                    aria-label="Remove contact"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {/* Per-contact portal-invite row: status pill on the
                    left, action button on the right. Deliberately kept
                    quiet visually so the primary "edit contact" flow
                    stays uncluttered. */}
                <div className="flex items-center justify-between gap-2 pl-1">
                  <div className="flex items-center gap-2 min-w-0">
                    {portalPill(r.email)}
                    {!canInvite && r.email && !r.name && (
                      <span className="text-[11px] text-slate-400">Add a name to invite</span>
                    )}
                    {!canInvite && !r.email && (
                      <span className="text-[11px] text-slate-400">Add an email to invite</span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs shrink-0"
                    onClick={() => sendContactInvite(r)}
                    disabled={!canInvite || busy || dirty}
                    data-testid={`customer-contact-invite-${i}`}
                    title={dirty ? 'Save your changes first' : ''}
                  >
                    <Send className="h-3 w-3 mr-1" />
                    {busy ? 'Sending…' : buttonLabel(r.email)}
                  </Button>
                </div>
                <textarea
                  value={r.notes || ''}
                  onChange={(e) => patchRow(i, 'notes', e.target.value)}
                  rows={2}
                  placeholder="Admin-only notes — birthdays, special considerations, gate codes, preferred times…"
                  className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs"
                  data-testid={`customer-contact-notes-${i}`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Portal-access controls for a single customer. Renders three states:
 *
 *   1. No email on file  — nothing we can send. Prompt admin to add one.
 *   2. Invite pending    — account created, user hasn't set a password
 *                          yet. Actions: resend invite, copy invite link.
 *   3. Portal active     — user has signed in at least once. Action:
 *                          mail them a password-reset link (used when
 *                          the customer says they can't log in).
 *
 * We deliberately expose *both* the "send email" action and a "copy
 * link to clipboard" fallback, because Resend on the free tier silently
 * refuses to deliver to un-verified domains — the copy path lets the
 * admin ping the user through Slack / SMS / WhatsApp when email fails.
 */
const PortalAccessCard = ({ customer, onSaved }) => {
  const [busy, setBusy] = React.useState(null);

  if (!customer) return null;
  const email = (customer.email || '').trim();
  const claimed = customer.inviteStatus === 'claimed';
  const hasEmail = !!email;

  const resendInvite = async () => {
    setBusy('resend');
    try {
      const res = await resendUserInvite(customer.id);
      if (res?.inviteEmailSent === false && res?.inviteUrl) {
        try { await navigator.clipboard.writeText(res.inviteUrl); } catch { /* ignore */ }
        toast.error('Email failed — invite link copied to your clipboard so you can share it directly.', { duration: 8000 });
      } else {
        toast.success(`Invite resent to ${email}`);
      }
      onSaved && onSaved({ ...customer, inviteStatus: 'pending' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend invite');
    } finally {
      setBusy(null);
    }
  };

  const copyInvite = async () => {
    setBusy('copy-invite');
    try {
      const { inviteUrl } = await getInviteLink(customer.id);
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied to clipboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate invite link');
    } finally {
      setBusy(null);
    }
  };

  const sendReset = async () => {
    setBusy('reset');
    try {
      const res = await sendUserPasswordReset(customer.id);
      // Reset link is always returned so we can offer clipboard-copy
      // regardless of email delivery outcome.
      if (res?.resetUrl) {
        try { await navigator.clipboard.writeText(res.resetUrl); } catch { /* ignore */ }
      }
      toast.success(`Password-reset email sent to ${email} — link also copied to your clipboard.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send password reset');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="portal-access-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-slate-500" />
          <h3 className="font-display uppercase tracking-wide text-sm text-slate-700">Portal access</h3>
          {!hasEmail && (
            <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
              No email on file
            </span>
          )}
          {hasEmail && !claimed && (
            <span className="text-[10px] uppercase tracking-widest bg-amber-50 text-amber-800 px-2 py-0.5 rounded" data-testid="portal-status-pending">
              Invite pending
            </span>
          )}
          {hasEmail && claimed && (
            <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded" data-testid="portal-status-active">
              Portal active
            </span>
          )}
        </div>
      </div>

      {!hasEmail && (
        <p className="text-sm text-slate-600">
          Add an email to this customer&rsquo;s record above to invite them to the portal.
        </p>
      )}

      {hasEmail && !claimed && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            An invite has been created for <span className="font-medium text-slate-900">{email}</span>,
            but they haven&rsquo;t set a password yet. Re-send the email or copy the link to share it directly.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={resendInvite}
              disabled={busy === 'resend'}
              data-testid="portal-resend-invite-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {busy === 'resend' ? 'Sending…' : 'Resend invite email'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={copyInvite}
              disabled={busy === 'copy-invite'}
              data-testid="portal-copy-invite-btn"
            >
              <Copy className="h-4 w-4 mr-2" />
              {busy === 'copy-invite' ? 'Copying…' : 'Copy invite link'}
            </Button>
          </div>
        </div>
      )}

      {hasEmail && claimed && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-900">{email}</span> can sign in.
            If they&rsquo;ve forgotten their password, send them a reset link — they can pick a new one from the email.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={sendReset}
              disabled={busy === 'reset'}
              data-testid="portal-send-reset-btn"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              {busy === 'reset' ? 'Sending…' : 'Send password reset'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


/**
 * Sub-customers: a Business customer can have child customers (e.g.
 * `Dave Tinsley Consultancy` owns `Cammell Laird` as a subcontracting
 * end-client). Each child is a full customer record in its own right —
 * clicking one opens its own detail page (with its own employees,
 * sites, invoices etc.). Children are linked via `parentCustomerId`.
 */
const SubCustomersCard = ({ parent, allCustomers, onReload }) => {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    company: '', name: '', email: '', phone: '', customerType: 'business',
  });
  const [saving, setSaving] = React.useState(false);
  const navigate = useNavigate();

  const subs = React.useMemo(() => (allCustomers || [])
    .filter((c) => c.parentCustomerId === parent?.id)
    .sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || '')),
    [allCustomers, parent?.id]);

  const create = async () => {
    const isBusiness = form.customerType === 'business';
    if (isBusiness && !form.company.trim()) return toast.error('Company is required for a business');
    if (!isBusiness && !form.name.trim()) return toast.error('Name is required for a private customer');
    setSaving(true);
    try {
      await createUser({
        name: isBusiness ? form.company.trim() : form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: isBusiness ? form.company.trim() : '',
        role: 'customer',
        customerType: form.customerType,
        parentCustomerId: parent.id,
      });
      toast.success('Sub-customer created');
      setOpen(false);
      setForm({ company: '', name: '', email: '', phone: '', customerType: 'business' });
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  if (!parent) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="sub-customers-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-display uppercase tracking-wide text-base text-slate-700">
            Sub-customers
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            End-clients you serve on behalf of <strong>{parent.company || parent.name}</strong>.
            Each sub-customer has its own site list, employees, invoices and quotes — click one to open it.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="sub-customer-add"
        >
          <Plus className="h-4 w-4 mr-1" /> Add sub-customer
        </Button>
      </div>

      {subs.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center">
          No sub-customers yet. Click <em>Add sub-customer</em> to link an end-client to this account.
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
          {subs.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/admin/customers/${s.id}`)}
              className="text-left border border-slate-200 rounded-md p-3 hover:border-slate-400 hover:bg-slate-50 transition"
              data-testid={`sub-customer-${s.id}`}
            >
              <div className="font-medium text-slate-900 truncate">
                {s.company || s.name || '(unnamed)'}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {s.email || 'no email'}
              </div>
              {s.customerType === 'private' && (
                <div className="mt-1 inline-flex text-[10px] font-medium text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded">
                  Private
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !saving && setOpen(o)}>
        <DialogContent className="sm:max-w-md" data-testid="sub-customer-dialog">
          <DialogHeader>
            <DialogTitle>Add sub-customer</DialogTitle>
            <DialogDescription>
              End-client of <strong>{parent.company || parent.name}</strong>. You&#39;ll be able to add their sites, employees, and send them EICRs / quotes / invoices independently.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select
                value={form.customerType}
                onValueChange={(v) => setForm({ ...form, customerType: v })}
              >
                <SelectTrigger className="mt-1" data-testid="sub-customer-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="private">Private (person)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.customerType === 'business' ? (
              <div>
                <Label className="text-xs">Company *</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="e.g. Cammell Laird"
                  className="mt-1"
                  data-testid="sub-customer-company"
                  autoFocus
                />
              </div>
            ) : (
              <div>
                <Label className="text-xs">Full name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Mike Adams"
                  className="mt-1"
                  data-testid="sub-customer-name"
                  autoFocus
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Email <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="contact@example.com"
                inputMode="email"
                className="mt-1"
                data-testid="sub-customer-email"
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+44…"
                inputMode="tel"
                className="mt-1"
                data-testid="sub-customer-phone"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={create}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="sub-customer-save"
            >
              {saving ? 'Saving…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
